/**
 * Wire schemas for module 4 (site planning): zones, crews, constraints, the
 * daily plan, visitors and action items (synced to the device), and the online
 * records — weekly plans and PPC, baselines, recovery plans, day close,
 * meetings, escalations, site instructions and productivity.
 *
 * Missing optional fields get safe defaults so a newer server cannot break an
 * older client. Decimals arrive as strings and stay strings unless coerced.
 */
import { z } from 'zod';

const id = z.string();
const nullableId = z.string().nullable().default(null);
const date = z.string().nullable().default(null);
const text = z.string().default('');
const version = z.number().int().nullable().default(null);
const count = z.coerce.number().catch(0).default(0);
const decimal = z.union([z.string(), z.number()]).nullable().default(null);

const party = z
  .object({ id: z.string(), name: z.string(), role: z.string() })
  .nullable()
  .catch(null)
  .default(null);
export type PartyRef = NonNullable<z.infer<typeof party>>;

// ---------------------------------------------------------------------------
// Shared vocabularies
// ---------------------------------------------------------------------------

/** Why planned work did not happen — constraints and weekly reviews share it (Pareto). */
export const REASONS = [
  'materials',
  'labour',
  'weather',
  'design_info',
  'predecessor',
  'equipment',
  'other',
] as const;
export type Reason = (typeof REASONS)[number];

export const ZONE_KINDS = ['block', 'level', 'area'] as const;
export type ZoneKind = (typeof ZONE_KINDS)[number];

// ---------------------------------------------------------------------------
// Synced (on the device)
// ---------------------------------------------------------------------------

export const zoneSchema = z.object({
  id,
  project_id: id,
  parent_id: nullableId,
  kind: z.enum(ZONE_KINDS).catch('area'),
  code: text,
  name: z.string(),
  path: text,
  sort: count,
  area_m2: decimal,
  notes: text,
  version,
});
export type Zone = z.infer<typeof zoneSchema>;

export const crewSchema = z.object({
  id,
  project_id: id,
  party_id: id,
  name: z.string(),
  trade: z.string().nullable().default(null),
  foreman_engagement_id: nullableId,
  foreman_member_id: nullableId,
  member_ids: z.array(z.string()).catch([]).default([]),
  headcount: count,
  size: count,
  status: z.string().default('active'),
  notes: text,
  version,
});
export type Crew = z.infer<typeof crewSchema>;

export const constraintSchema = z.object({
  id,
  project_id: id,
  reference: text,
  task_id: nullableId,
  zone_id: nullableId,
  stage_id: nullableId,
  kind: z.enum(REASONS).catch('other'),
  description: z.string(),
  owner_id: nullableId,
  due_date: date,
  /** open → cleared (online, by the planner or the owner) */
  status: z.string().default('open'),
  raised_by_id: nullableId,
  raised_by_party_id: nullableId,
  cleared_at: date,
  clearance_note: text,
  created_at: date,
  version,
});
export type Constraint = z.infer<typeof constraintSchema>;
export const CONSTRAINT_EDITABLE = [
  'kind',
  'description',
  'task_id',
  'zone_id',
  'stage_id',
  'owner_id',
  'due_date',
] as const;

export const dailyAssignmentSchema = z.object({
  id,
  project_id: id,
  plan_date: z.string(),
  crew_id: nullableId,
  task_id: nullableId,
  zone_id: nullableId,
  activity: text,
  workers_planned: z.number().int().nullable().default(null),
  target_output: text,
  notes: text,
  planned_by_id: nullableId,
  /** Removed (a tombstone on its way); hidden from the plan. */
  removed: z.boolean().default(false),
  created_at: date,
  version,
});
export type DailyAssignment = z.infer<typeof dailyAssignmentSchema>;
export const ASSIGNMENT_EDITABLE = [
  'crew_id',
  'task_id',
  'zone_id',
  'activity',
  'workers_planned',
  'target_output',
  'notes',
] as const;

export const VISIT_PURPOSES = [
  'inspection',
  'meeting',
  'delivery',
  'client',
  'authority',
  'other',
] as const;
export type VisitPurpose = (typeof VISIT_PURPOSES)[number];

export const visitSchema = z.object({
  id,
  project_id: id,
  visitor_name: z.string(),
  organisation: text,
  phone: text,
  purpose: z.enum(VISIT_PURPOSES).catch('other'),
  host_id: nullableId,
  host_name: text,
  time_in: z.string(),
  time_out: date,
  visit_date: date,
  induction_given: z.boolean().default(false),
  ppe_issued: z.boolean().default(false),
  notes: text,
  version,
});
export type Visit = z.infer<typeof visitSchema>;
export const VISIT_EDITABLE = [
  'visitor_name',
  'organisation',
  'phone',
  'purpose',
  'host_id',
  'host_name',
  'time_out',
  'induction_given',
  'ppe_issued',
  'notes',
] as const;

export const actionItemSchema = z.object({
  id,
  project_id: id,
  reference: text,
  meeting_id: nullableId,
  description: z.string(),
  owner_member_id: nullableId,
  owner_user_id: nullableId,
  owner_party_id: nullableId,
  owner_name: text,
  due_date: date,
  /** open → done (offline, by its owner) | cancelled (online) */
  status: z.string().default('open'),
  raised_by_id: nullableId,
  raised_by_party_id: nullableId,
  done_at: date,
  status_note: text,
  client_visible: z.boolean().default(false),
  created_at: date,
  version,
});
export type ActionItem = z.infer<typeof actionItemSchema>;

// ---------------------------------------------------------------------------
// Online: weekly plans, PPC, baselines, recovery plans
// ---------------------------------------------------------------------------

export const commitmentSchema = z.object({
  id,
  task_id: nullableId,
  description: z.string(),
  crew_id: nullableId,
  outcome: z.enum(['done', 'not_done']).nullable().catch(null).default(null),
  reason: z.enum(REASONS).nullable().catch(null).default(null),
  reason_note: text,
});
export type Commitment = z.infer<typeof commitmentSchema>;

export const weeklyPlanSchema = z.object({
  id,
  project_id: id,
  week_start: z.string(),
  /** draft → committed → reviewed */
  status: z.string().default('draft'),
  notes: text,
  committed_at: date,
  reviewed_at: date,
  ppc: z.coerce.number().nullable().catch(null).default(null),
  commitments: z.array(commitmentSchema).catch([]).default([]),
  version,
});
export type WeeklyPlan = z.infer<typeof weeklyPlanSchema>;

export const ppcHistorySchema = z.object({
  weeks: z
    .array(
      z.object({
        plan_id: z.string(),
        week_start: z.string(),
        done: count,
        reviewed: count,
        ppc: z.coerce.number().nullable().catch(null).default(null),
      }),
    )
    .catch([])
    .default([]),
  average_ppc: z.coerce.number().nullable().catch(null).default(null),
  pareto: z
    .array(
      z.object({
        reason: z.enum(REASONS).catch('other'),
        count,
        percent: z.coerce.number().catch(0),
        cumulative_percent: z.coerce.number().catch(0),
      }),
    )
    .catch([])
    .default([]),
  misses: count,
});
export type PpcHistory = z.infer<typeof ppcHistorySchema>;

export const baselineSchema = z.object({
  id,
  project_id: id,
  name: z.string(),
  captured_at: z.string(),
  captured_by: text,
  stages: count,
  tasks: count,
});
export type Baseline = z.infer<typeof baselineSchema>;

const slipRow = z.object({
  id: z.string(),
  status: z.string().default(''),
  baseline_start: date,
  baseline_end: date,
  current_start: date,
  current_end: date,
  slip_days: z.number().nullable().default(null),
  added: z.boolean().default(false),
});

export const comparisonSchema = z.object({
  baseline: z.object({ id: z.string(), name: z.string(), captured_at: z.string() }),
  as_of: z.string(),
  stages: z
    .array(
      slipRow.extend({ name: z.string(), start_slip_days: z.number().nullable().default(null) }),
    )
    .catch([])
    .default([]),
  tasks: z
    .array(slipRow.extend({ title: z.string(), stage_id: nullableId }))
    .catch([])
    .default([]),
  summary: z.object({
    stages_late: count,
    worst_slip_days: count,
    baseline_finish: date,
    current_finish: date,
    finish_slip_days: z.number().nullable().default(null),
  }),
  late_chains: z
    .array(
      z.object({
        task_id: z.string(),
        title: z.string(),
        due_date: date,
        days_overdue: count,
        chain: z
          .array(
            z.object({
              id: z.string(),
              title: z.string(),
              status: z.string(),
              due_date: date,
              days_overdue: count,
            }),
          )
          .catch([])
          .default([]),
      }),
    )
    .catch([])
    .default([]),
});
export type Comparison = z.infer<typeof comparisonSchema>;

// ---------------------------------------------------------------------------
// Critical path (GET /projects/{id}/programme/critical-path)
// ---------------------------------------------------------------------------

const cpmRow = z.object({
  id: z.string(),
  name: z.string().default(''),
  status: z.string().default(''),
  included: z.boolean().default(false),
  /** no_dates | finish_before_start | loop */
  excluded_reason: z.string().nullable().default(null),
  done: z.boolean().default(false),
  duration_days: z.number().nullable().default(null),
  planned_start: date,
  planned_finish: date,
  early_start: date,
  early_finish: date,
  late_start: date,
  late_finish: date,
  total_float_days: z.number().nullable().default(null),
  free_float_days: z.number().nullable().default(null),
  critical: z.boolean().default(false),
  /** planned_start | predecessor | today | actual */
  start_reason: z.string().default(''),
  driven_by: z.array(z.string()).catch([]).default([]),
  predecessors: z.array(z.string()).catch([]).default([]),
  notes: z.array(z.string()).catch([]).default([]),
});

export const criticalPathSchema = z.object({
  as_of: z.string(),
  calendar: z.string().default('calendar_days'),
  method: text,
  constraints_visible: z.boolean().default(false),
  summary: z.object({
    finish: date,
    target_completion: date,
    days_beyond_target: z.number().nullable().default(null),
    tasks_included: count,
    tasks_excluded: count,
    critical_tasks: count,
    critical_paths: count,
    open_constraints_on_critical: count,
  }),
  tasks: z
    .array(
      cpmRow.extend({
        stage_id: nullableId,
        /** null when the reader may not see constraints (the owner side). */
        open_constraints: z
          .array(
            z.object({
              id: z.string(),
              number: count,
              kind: text,
              description: text,
              due_date: date,
            }),
          )
          .nullable()
          .catch(null)
          .default(null),
      }),
    )
    .catch([])
    .default([]),
  paths: z.array(z.array(z.string())).catch([]).default([]),
  drives_finish: z
    .array(
      z.object({
        starts_because: z.string().default(''),
        steps: z
          .array(
            z.object({
              id: z.string(),
              name: z.string().default(''),
              early_start: date,
              early_finish: date,
              duration_days: z.number().nullable().default(null),
            }),
          )
          .catch([])
          .default([]),
      }),
    )
    .catch([])
    .default([]),
  stages: z
    .object({
      finish: date,
      rows: z.array(cpmRow).catch([]).default([]),
      paths: z.array(z.array(z.string())).catch([]).default([]),
    })
    .catch({ finish: null, rows: [], paths: [] })
    .default({ finish: null, rows: [], paths: [] }),
});
export type CriticalPath = z.infer<typeof criticalPathSchema>;
export type CriticalPathTask = CriticalPath['tasks'][number];

export const RECOVERY_STATUSES = ['draft', 'in_progress', 'recovered', 'abandoned'] as const;

export const recoveryPlanSchema = z.object({
  id,
  project_id: id,
  task_id: nullableId,
  stage_id: nullableId,
  title: z.string(),
  days_behind: count,
  measures: text,
  extra_labour: count,
  target_date: date,
  status: z.enum(RECOVERY_STATUSES).catch('draft'),
  created_at: date,
  version,
});
export type RecoveryPlan = z.infer<typeof recoveryPlanSchema>;

// ---------------------------------------------------------------------------
// Online: day close, visitor counts
// ---------------------------------------------------------------------------

export const DAY_CHECKS = ['attendance', 'diary', 'progress', 'tomorrow'] as const;
export type DayCheckKey = (typeof DAY_CHECKS)[number];

const check = z
  .object({
    complete: z.boolean().default(false),
    link: z.string().default(''),
    workers: count,
    open_check_ins: count,
    entries: count,
    submitted: count,
    status: z.string().nullable().optional(),
    updates: count,
    assignments: count,
    date: date,
  })
  .partial()
  .passthrough();

export const dayCloseSchema = z.object({
  id,
  project_id: id,
  close_date: z.string(),
  closed_by: text,
  closed_at: z.string(),
  checks: z.record(z.string(), check).catch({}).default({}),
  missing: z.array(z.string()).catch([]).default([]),
  missing_note: text,
  notes: text,
});
export type DayClose = z.infer<typeof dayCloseSchema>;

export const dayStatusSchema = z.object({
  date: z.string(),
  checks: z.object({
    attendance: check,
    diary: check,
    progress: check,
    tomorrow: check,
  }),
  missing: z.array(z.enum(DAY_CHECKS)).catch([]).default([]),
  close: dayCloseSchema.nullable().default(null),
});
export type DayStatus = z.infer<typeof dayStatusSchema>;

export const visitSummarySchema = z.object({
  since: z.string(),
  total: count,
  today: count,
  on_site_now: count,
  by_purpose: z.record(z.string(), z.number()).catch({}).default({}),
});
export type VisitSummary = z.infer<typeof visitSummarySchema>;

// ---------------------------------------------------------------------------
// Online: meetings, escalations, site instructions
// ---------------------------------------------------------------------------

export const MEETING_KINDS = ['site', 'progress', 'coordination', 'safety'] as const;
export type MeetingKind = (typeof MEETING_KINDS)[number];

export const attendeeSchema = z.object({
  member_id: z.string().optional(),
  name: z.string().optional(),
  organisation: z.string().optional(),
  present: z.boolean().default(true),
});
export type Attendee = z.infer<typeof attendeeSchema>;

export const meetingSchema = z.object({
  id,
  project_id: id,
  reference: text,
  kind: z.enum(MEETING_KINDS).catch('site'),
  title: z.string(),
  meeting_date: z.string(),
  start_time: z.string().nullable().default(null),
  location: text,
  chair_id: nullableId,
  attendees: z.array(attendeeSchema).catch([]).default([]),
  agenda: text,
  minutes: text,
  decisions: z.array(z.string()).catch([]).default([]),
  /** scheduled → held → minutes_issued | cancelled */
  status: z.string().default('scheduled'),
  organiser: text,
  organiser_party: party,
  minutes_issued_at: date,
  client_visible: z.boolean().default(false),
  actions: z.array(actionItemSchema).optional(),
  version,
});
export type Meeting = z.infer<typeof meetingSchema>;

export const ESCALATION_SOURCES = ['constraint', 'issue', 'ncr', 'task', 'other'] as const;

export const escalationSchema = z.object({
  id,
  project_id: id,
  reference: text,
  source_type: z.enum(ESCALATION_SOURCES).catch('other'),
  source_id: nullableId,
  source_label: text,
  title: z.string(),
  description: text,
  level: count,
  to_party: party,
  to_role: z.string().nullable().default(null),
  due_date: date,
  /** open → responded → closed */
  status: z.string().default('open'),
  raised_by: text,
  raised_by_id: nullableId,
  raised_by_party: party,
  response: text,
  responded_by: text,
  responded_at: date,
  closed_at: date,
  created_at: date,
  version,
});
export type Escalation = z.infer<typeof escalationSchema>;

export const instructionSchema = z.object({
  id,
  project_id: id,
  reference: text,
  subject: z.string(),
  description: text,
  location: text,
  zone_id: nullableId,
  reference_note: text,
  document_ids: z.array(z.string()).catch([]).default([]),
  documents: z
    .array(z.object({ id: z.string(), title: z.string(), reference: text, category: text }))
    .catch([])
    .default([]),
  cost_impact_expected: z.boolean().default(false),
  time_impact_expected: z.boolean().default(false),
  complete_by: date,
  /** issued → acknowledged → actioned → closed */
  status: z.string().default('issued'),
  issued_by: text,
  issued_by_id: nullableId,
  issued_by_party: party,
  to_party: party,
  issued_at: z.string(),
  acknowledged_by: text,
  acknowledged_at: date,
  acknowledgement_note: text,
  actioned_at: date,
  actioned_note: text,
  closed_at: date,
  variation_id: nullableId,
  variation_reference: z.string().nullable().default(null),
  version,
});
export type Instruction = z.infer<typeof instructionSchema>;

// ---------------------------------------------------------------------------
// Online: productivity (contractor side)
// ---------------------------------------------------------------------------

const rate = z.coerce.number().nullable().catch(null).default(null);

export const productivitySchema = z.object({
  from: z.string(),
  to: z.string(),
  weeks: z.array(z.string()).catch([]).default([]),
  crews: z
    .array(
      z.object({
        crew_id: z.string(),
        crew: z.string(),
        trade: text,
        week_start: z.string(),
        unit: text,
        quantity: z.coerce.number().catch(0),
        labour_hours: z.coerce.number().catch(0),
        output_per_hour: rate,
      }),
    )
    .catch([])
    .default([]),
  trades: z
    .array(
      z.object({
        trade: text,
        week_start: z.string(),
        unit: text,
        quantity: z.coerce.number().catch(0),
        labour_hours: z.coerce.number().catch(0),
        output_per_hour: rate,
      }),
    )
    .catch([])
    .default([]),
  boq: z
    .array(
      z.object({
        lineage: z.string(),
        item_code: text,
        description: text,
        unit: text,
        boq_quantity: z.coerce.number().catch(0),
        done_to_date: z.coerce.number().catch(0),
        percent: rate,
      }),
    )
    .catch([])
    .default([]),
  notes: z.array(z.string()).catch([]).default([]),
});
export type Productivity = z.infer<typeof productivitySchema>;
